import { getTreeRowBlotterGroupColDef, stringColumnComparatorMultiColumn } from '@talos/kyoko';
import type { GetQuickFilterTextParams, GridOptions } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import type { CareOrderBlotterEntity } from '../types';
import { legRowFadedCellClassRules } from './legRowFadedCellClassRules';

const careOrderBlotterEntityComparator = stringColumnComparatorMultiColumn<CareOrderBlotterEntity>(['Symbol']);

export const useCareOrderBlotterAutoGroupColDef = () => {
  const autoGroupColumnDef = useMemo<GridOptions<CareOrderBlotterEntity>['autoGroupColumnDef']>(
    () =>
      ({
        headerName: 'ID',
        editable: false,
        pinned: 'left',
        sortable: true,
        suppressColumnsToolPanel: false,
        cellRendererParams: {
          suppressCount: true,
        },
        comparator: (valueA, valueB, nodeA, nodeB) => {
          if (nodeA.data == null || nodeB.data == null) {
            return 0;
          }

          return careOrderBlotterEntityComparator(nodeA.data, nodeB.data);
        },
        width: 106, // minimum width to have 3 levels of abbreviated ids visible
        cellClassRules: legRowFadedCellClassRules,
        getQuickFilterText: (params: GetQuickFilterTextParams<CareOrderBlotterEntity, string>) => {
          // We want to be able to search for child row (Order/Quote) IDs and see that child + parent. By default, when searching,
          // if the parent row doesnt match then it and _all its children_ are removed from view. To get around this, we include any children
          // ids into the quickFilterText of the parent row too.
          if (params.node.allLeafChildren && params.node.allLeafChildren.length > 0) {
            return compact([params.value, ...params.node.allLeafChildren.map(child => child.data?.id)]).join('__');
          }
          // Else we do not have any children, just return our own value for quickfiltering
          return params.value ?? '';
        },
        ...getTreeRowBlotterGroupColDef<CareOrderBlotterEntity>(),
      }) satisfies GridOptions<CareOrderBlotterEntity>['autoGroupColumnDef'],
    []
  );

  return autoGroupColumnDef;
};
